import React, { useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";
import "../../VideoManager.css";
import { ensureMandatoryTag, MANDATORY_TAG } from "../../utils/videoManagerUtils";
import uploadVideoEndpoint from "../../config/uploadApi";

const KARAOKE_MODES = [
  { id: "full", label: "Full vocal removal" },
  { id: "instrumental", label: "Instrumental + limited vocals" },
  { id: "custom", label: "Custom EQ removal" },
];

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const audioBufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let sampleIndex = 0; sampleIndex < buffer.length; sampleIndex += 1) {
    for (let channel = 0; channel < numChannels; channel += 1) {
      const sample = buffer.getChannelData(channel)[sampleIndex];
      const clamped = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
      offset += bytesPerSample;
    }
  }
  return arrayBuffer;
};

const removeVocalsFromArrayBuffer = async (arrayBuffer) => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  const context = new AudioContextClass();
  const decoded = await context.decodeAudioData(arrayBuffer.slice(0));

  const leftChannel = decoded.numberOfChannels > 0 ? decoded.getChannelData(0) : new Float32Array(decoded.length);
  const rightChannel =
    decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : leftChannel;

  const outputBuffer = context.createBuffer(2, decoded.length, decoded.sampleRate);
  const destinationLeft = outputBuffer.getChannelData(0);
  const destinationRight = outputBuffer.getChannelData(1);
  const isStereo = decoded.numberOfChannels > 1;

  for (let i = 0; i < decoded.length; i += 1) {
    const leftSample = leftChannel[i] || 0;
    const rightSample = rightChannel[i] || 0;
    let processedSample;

    if (isStereo) {
      const difference = leftSample - rightSample;
      processedSample = difference * 1.5;
    } else {
      const previous = i > 0 ? leftChannel[i - 1] || 0 : 0;
      processedSample = (leftSample - previous * 0.6) * 1.2;
    }

    const normalized = Math.max(-1, Math.min(1, processedSample));
    destinationLeft[i] = normalized;
    destinationRight[i] = normalized;
  }

  context.close();
  return outputBuffer;
};

const createKaraokeStem = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const processedBuffer = await removeVocalsFromArrayBuffer(arrayBuffer);
  const wavBuffer = audioBufferToWav(processedBuffer);
  const blob = new Blob([wavBuffer], { type: "audio/wav" });
  const url = URL.createObjectURL(blob);
  return { blob, url };
};

const extractYouTubeID = (value) => {
  if (!value) return "";
  const match = value.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
  );
  return match ? match[1] : "";
};

const fetchYouTubeAudio = async (videoId) => {
  const apiUrl = `https://www.yt-download.org/api/button/mp3/${videoId}`;
  const proxyUrl = "https://thingproxy.freeboard.io/fetch/";
  const response = await fetch(proxyUrl + encodeURIComponent(apiUrl));
  if (!response.ok) {
    throw new Error("Unable to reach the YouTube extraction helper.");
  }
  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const mp3Anchor = doc.querySelector("a[href$='.mp3']");
  if (!mp3Anchor?.href) {
    throw new Error("No downloadable MP3 link was found.");
  }
  const audioResponse = await fetch(mp3Anchor.href);
  if (!audioResponse.ok) {
    throw new Error("Failed to download the YouTube audio.");
  }
  const arrayBuffer = await audioResponse.arrayBuffer();
  return new Blob([arrayBuffer], { type: "audio/mpeg" });
};

const sanitizeDownloadName = (value, fallback, extension = ".webm") => {
  const base = (value || fallback || "gcc-studio").replace(/\.[^.]+$/, "").replace(/[^a-z0-9-]+/gi, "-");
  return `${base}${extension}`;
};

export default function VideoEditingStudio() {
  const [uploadedSource, setUploadedSource] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  const [latestRecording, setLatestRecording] = useState(null);
  const [recordingImportHint, setRecordingImportHint] = useState("No recording imported yet.");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(90);
  const [overlayText, setOverlayText] = useState("Sing along with GCC");
  const [useFileNameOverlay, setUseFileNameOverlay] = useState(false);
  const [karaokeMode, setKaraokeMode] = useState(KARAOKE_MODES[0].id);
  const [karaokeReady, setKaraokeReady] = useState(false);
  const [karaokeProcessing, setKaraokeProcessing] = useState(false);
  const [karaokeStatus, setKaraokeStatus] = useState("");
  const [editorNotes, setEditorNotes] = useState("");
  const [uploadTitle, setUploadTitle] = useState("GCC Video Studio Cut");
  const [uploadDescription, setUploadDescription] = useState(
    "Edited worship video with karaoke track built inside GCC Studio.",
  );
  const [tags, setTags] = useState(ensureMandatoryTag(["video-edit", "karaoke"]));
  const [tagInput, setTagInput] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedVideoLink, setUploadedVideoLink] = useState("");
  const [karaokeSourceMode, setKaraokeSourceMode] = useState("upload");
  const [activeSourceFile, setActiveSourceFile] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeVideoId, setYoutubeVideoId] = useState("");
  const [youtubeSourceFile, setYoutubeSourceFile] = useState(null);
  const [youtubeFetchError, setYoutubeFetchError] = useState("");
  const [isFetchingYoutubeAudio, setIsFetchingYoutubeAudio] = useState(false);
  const [showEditPreview, setShowEditPreview] = useState(false);
  const [processedAudioUrl, setProcessedAudioUrl] = useState("");
  const [processingError, setProcessingError] = useState("");
  const [processedBlob, setProcessedBlob] = useState(null);
  const [karaokeDownloadName, setKaraokeDownloadName] = useState("karaoke-stem.wav");

  const previewUrlRef = useRef("");
  const processedUrlRef = useRef("");

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      if (processedUrlRef.current) {
        URL.revokeObjectURL(processedUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      const detail = event?.detail;
      if (!detail?.blob) return;
      setLatestRecording({ blob: detail.blob, name: detail.fileName || "gcc-recording.webm" });
      setRecordingImportHint(`Recorded clip ready: ${detail.fileName || "recent capture"}`);
    };
    window.addEventListener("gccRecordingReady", handler);
    if (window.gccLatestRecording?.blob) {
      setLatestRecording(window.gccLatestRecording);
      setRecordingImportHint(`Recorded clip ready: ${window.gccLatestRecording.fileName || "recent capture"}`);
    }
    return () => window.removeEventListener("gccRecordingReady", handler);
  }, []);

  const overlayTitle = useFileNameOverlay && uploadedSource?.name
    ? uploadedSource.name.replace(/\.[^.]+$/, "")
    : overlayText;

  const finalizePreviewUrl = (file) => {
    if (!file) return;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = URL.createObjectURL(file);
    setVideoPreviewUrl(previewUrlRef.current);
  };

  const handleSourceUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadedSource(file);
    setKaraokeReady(false);
    setKaraokeStatus("");
    finalizePreviewUrl(file);
    setProcessedAudioUrl("");
    setProcessingError("");
    if (karaokeSourceMode === "upload") {
      setActiveSourceFile(file);
    }
    setUploadTitle(file.name.replace(/\.[^.]+$/, ""));
  };

  const importLatestRecording = () => {
    if (!latestRecording?.blob) {
      setRecordingImportHint("No recording ready yet.");
      return;
    }
    const recordingFile = new File([latestRecording.blob], latestRecording.name, {
      type: latestRecording.blob.type || "video/webm",
    });
    setUploadedSource(recordingFile);
    finalizePreviewUrl(recordingFile);
    setKaraokeReady(false);
    setKaraokeStatus("Recording imported into this studio.");
    setActiveSourceFile(recordingFile);
    setProcessingError("");
    setUploadTitle(recordingFile.name.replace(/\.[^.]+$/, ""));
    setRecordingImportHint(`Imported ${recordingFile.name}.`);
  };

  const handleKaraokeSourceModeChange = (mode) => {
    setKaraokeSourceMode(mode);
    setKaraokeStatus("");
    if (mode === "upload") {
      setActiveSourceFile(uploadedSource);
    } else if (mode === "youtube") {
      setActiveSourceFile(youtubeSourceFile);
    }
  };

  const handleFetchYoutubeAudio = async () => {
    const id = extractYouTubeID(youtubeUrl.trim());
    if (!id) {
      setYoutubeFetchError("Provide a valid YouTube URL or ID.");
      return;
    }
    setYoutubeFetchError("");
    setIsFetchingYoutubeAudio(true);
    try {
      const blob = await fetchYouTubeAudio(id);
      const file = new File([blob], `${id}.mp3`, { type: "audio/mpeg" });
      setYoutubeVideoId(id);
      setYoutubeSourceFile(file);
      setActiveSourceFile(file);
      setKaraokeSourceMode("youtube");
      setKaraokeStatus("YouTube audio downloaded. Build the karaoke stem now.");
    } catch (error) {
      console.error("YouTube fetch failed", error);
      setYoutubeFetchError(error.message || "Unable to download the audio track.");
    } finally {
      setIsFetchingYoutubeAudio(false);
    }
  };

  const handleBuildKaraoke = async () => {
    const sourceFile = activeSourceFile || uploadedSource;
    if (!sourceFile) {
      setKaraokeStatus("Select a source before creating the karaoke stem.");
      return;
    }
    setKaraokeProcessing(true);
    setKaraokeStatus("Removing vocals for karaoke overlay...");
    setProcessingError("");
    setKaraokeReady(false);
    try {
      const processed = await createKaraokeStem(sourceFile);
      if (processedUrlRef.current) {
        URL.revokeObjectURL(processedUrlRef.current);
      }
      processedUrlRef.current = processed.url;
      setProcessedBlob(processed.blob);
      setProcessedAudioUrl(processed.url);
      setKaraokeReady(true);
      setKaraokeStatus("Karaoke track ready. Preview it below.");
      setKaraokeDownloadName(`karaoke-stem-${Date.now()}.wav`);
    } catch (error) {
      console.error("Karaoke processing failed", error);
      setProcessingError(error.message || "Vocal removal failed.");
      setKaraokeStatus("Failed to build the karaoke stem.");
    } finally {
      setKaraokeProcessing(false);
    }
  };

  const handleUploadRequest = async () => {
    if (!uploadedSource) {
      setUploadStatus("Upload footage before sending a studio edit to YouTube.");
      return;
    }
    setIsUploading(true);
    setUploadStatus("Sending studio edit to @gcc-worshop…");
    setUploadedVideoLink("");
    const formData = new FormData();
    formData.append("video", uploadedSource, uploadedSource.name);
    formData.append("title", uploadTitle);
    formData.append(
      "description",
      `${uploadDescription}\n\nEditor notes: ${editorNotes}\nKaraoke mode: ${karaokeMode}`,
    );
    formData.append("privacy", "unlisted");
    formData.append("tags", JSON.stringify(ensureMandatoryTag(tags)));

    try {
      const res = await fetch(uploadVideoEndpoint, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || "Upload endpoint rejected the file.");
      }
      const payload = await res.json();
      setUploadedVideoLink(payload.videoUrl);
      const karaokeNote = karaokeReady ? "karaoke stem applied" : "original audio";
      setUploadStatus(`Upload complete (${karaokeNote}). View ${payload.videoUrl}`);
    } catch (error) {
      console.error("Studio upload failed", error);
      setUploadStatus(`Upload failed: ${error.message || "Server error"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleTrimChange = (key, value) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return;
    if (key === "start") {
      setTrimStart(Math.max(0, Math.min(numericValue, trimEnd - 1)));
    } else {
      setTrimEnd(Math.max(trimStart + 1, numericValue));
    }
  };

  const handleAddTag = () => {
    const candidate = tagInput.trim();
    setTagInput("");
    if (!candidate) return;
    setTags((state) => ensureMandatoryTag([...state, candidate]));
  };

  const videoDownloadName = sanitizeDownloadName(uploadTitle || uploadedSource?.name, "studio-edit");
  const karaokeDownloadLink = processedAudioUrl;

  return (
    <section className="studio-panel">
      <div className="studio-panel__dual-grid video-studio__layout">
        <div className="studio-panel__controls-column">
          <div className="studio-panel__control-row">
            <label className="studio-panel__file-control">
              <span>Upload video/audio</span>
              <input type="file" accept="video/*,audio/*" onChange={handleSourceUpload} />
            </label>
            <span className="studio-panel__status">
              {uploadedSource ? `Loaded ${uploadedSource.name}` : "No source loaded yet."}
            </span>
          </div>
          <button
            type="button"
            className="studio-panel__cta studio-panel__cta--secondary"
            onClick={importLatestRecording}
            disabled={!latestRecording}
          >
            Use latest recording
          </button>
          <p className="studio-panel__helper">{recordingImportHint}</p>

          <div className="studio-panel__control-block">
            <div className="recording-studio__label">Build karaoke from</div>
            <label>
              <input
                type="radio"
                name="karaokeSource"
                value="upload"
                checked={karaokeSourceMode === "upload"}
                onChange={() => handleKaraokeSourceModeChange("upload")}
              />
              <span>Local upload</span>
            </label>
            <label>
              <input
                type="radio"
                name="karaokeSource"
                value="youtube"
                checked={karaokeSourceMode === "youtube"}
                onChange={() => handleKaraokeSourceModeChange("youtube")}
              />
              <span>YouTube audio</span>
            </label>
            <p className="studio-panel__helper">
              Active source: {activeSourceFile?.name || "None selected"}
            </p>
          </div>

          <div className="studio-panel__control-block">
            <div className="recording-studio__label">YouTube fallback</div>
            <input
              value={youtubeUrl}
              onChange={(event) => setYoutubeUrl(event.target.value)}
              placeholder="Paste YouTube URL or ID"
            />
            <button
              type="button"
              className="studio-panel__cta studio-panel__cta--secondary"
              onClick={handleFetchYoutubeAudio}
              disabled={isFetchingYoutubeAudio}
            >
              {isFetchingYoutubeAudio ? "Fetching audio…" : "Download YouTube audio"}
            </button>
            {youtubeFetchError && (
              <p className="studio-panel__helper studio-panel__helper--error">
                {youtubeFetchError}
              </p>
            )}
          </div>

          <div className="studio-panel__options">
            <label>
              <span>Trim start (seconds)</span>
              <input
                type="number"
                min={0}
                value={trimStart}
                onChange={(event) => handleTrimChange("start", event.target.value)}
              />
            </label>
            <label>
              <span>Trim end (seconds)</span>
              <input
                type="number"
                min={trimStart + 1}
                value={trimEnd}
                onChange={(event) => handleTrimChange("end", event.target.value)}
              />
            </label>
            <label>
              <span>Overlay text</span>
              <input value={overlayText} onChange={(event) => setOverlayText(event.target.value)} />
            </label>
            <label className="studio-panel__checkbox-label">
              <input
                type="checkbox"
                checked={useFileNameOverlay}
                onChange={(event) => setUseFileNameOverlay(event.target.checked)}
              />
              <span>Use source file name as overlay</span>
            </label>
          </div>

          <div className="studio-panel__control-block">
            <div className="studio-panel__karaoke-headline">
              <h3>Karaoke Builder</h3>
              <p>Strip vocals and pair the stem with captions or overlay text.</p>
            </div>
            <div className="studio-panel__karaoke-modes">
              {KARAOKE_MODES.map((mode) => (
                <label key={mode.id}>
                  <input
                    type="radio"
                    name="karaokeMode"
                    value={mode.id}
                    checked={karaokeMode === mode.id}
                    onChange={(event) => setKaraokeMode(event.target.value)}
                  />
                  <span>{mode.label}</span>
                </label>
              ))}
            </div>
            <button
              type="button"
              className="studio-panel__cta"
              onClick={handleBuildKaraoke}
              disabled={karaokeProcessing}
            >
              {karaokeProcessing ? "Building karaoke…" : "Build karaoke stem"}
            </button>
            <p className="studio-panel__helper">{karaokeStatus || "Karaoke is waiting for your edits."}</p>
            {karaokeReady && (
              <p className="studio-panel__helper">Karaoke ready. Layer captions, finalize scenes, then upload.</p>
            )}
            {processingError && (
              <p className="studio-panel__helper">Processing error: {processingError}</p>
            )}
          </div>
        </div>

        <div className="studio-panel__preview-column">
          {videoPreviewUrl && (
            <div className="recording-studio__preview-card">
              <div className="recording-studio__label">Preview</div>
              {showEditPreview ? (
                <>
                  <video
                    src={videoPreviewUrl}
                    controls
                    loop
                    className="studio-panel__video-preview"
                    playsInline
                  />
                  <div className="studio-panel__preview-info">
                    <p>Overlay: {overlayTitle}</p>
                    <p>Trim range: {trimStart}s – {trimEnd}s</p>
                  </div>
                </>
              ) : (
                <div className="recording-studio__placeholder">Preview trimmed edit available</div>
              )}
              <div className="studio-panel__preview-actions">
                <button
                  type="button"
                  className="studio-panel__cta studio-panel__cta--secondary"
                  onClick={() => setShowEditPreview((prev) => !prev)}
                >
                  {showEditPreview ? "Hide preview" : "Show preview"}
                </button>
                {showEditPreview && (
                  <a
                    className="studio-panel__cta studio-panel__cta--secondary"
                    href={videoPreviewUrl}
                    download={videoDownloadName}
                  >
                    Download edit
                  </a>
                )}
              </div>
            </div>
          )}

          {youtubeVideoId && (
            <div className="studio-panel__youtube-preview-wrapper">
              <YouTube
                videoId={youtubeVideoId}
                className="studio-panel__youtube-preview"
                opts={{ width: "100%", height: "200", playerVars: { autoplay: 0 } }}
              />
            </div>
          )}

          {processedAudioUrl && (
            <div className="studio-panel__karaoke-preview">
              <p>Karaoke stem preview</p>
              <audio controls src={processedAudioUrl} className="studio-panel__audio-preview" />
              <div className="studio-panel__preview-actions">
                <a
                  href={processedAudioUrl}
                  download={karaokeDownloadName}
                  className="studio-panel__cta studio-panel__cta--secondary"
                >
                  Download karaoke stem
                </a>
              </div>
            </div>
          )}

          <form
            className="studio-panel__form"
            onSubmit={(event) => {
              event.preventDefault();
              handleUploadRequest();
            }}
          >
            <label>
              <span>Title</span>
              <input value={uploadTitle} onChange={(event) => setUploadTitle(event.target.value)} />
            </label>
            <label>
              <span>Description</span>
              <textarea
                rows={3}
                value={uploadDescription}
                onChange={(event) => setUploadDescription(event.target.value)}
              />
            </label>
            <label>
              <span>Editor notes</span>
              <textarea
                rows={2}
                value={editorNotes}
                onChange={(event) => setEditorNotes(event.target.value)}
                placeholder="Document trims, overlays, karaoke specifics..."
              />
            </label>
            <label>
              <span>Tags</span>
              <div className="studio-panel__tag-row">
                <input
                  placeholder="Add expressive tags"
                  value={tagInput}
                  onChange={(event) => setTagInput(event.target.value)}
                />
                <button type="button" onClick={handleAddTag}>
                  Add tag
                </button>
              </div>
            </label>
            <div className="studio-panel__tag-list">
              {ensureMandatoryTag(tags).map((tag) => (
                <span key={tag} className="studio-panel__tag">
                  {tag}
                </span>
              ))}
            </div>
            <button type="submit" className="studio-panel__cta" disabled={isUploading}>
              {isUploading ? "Uploading…" : "Upload studio edit to @gcc-worshop"}
            </button>
            {uploadStatus && <p className="studio-panel__helper">{uploadStatus}</p>}
            {uploadedVideoLink && (
              <a className="studio-panel__helper" href={uploadedVideoLink} target="_blank" rel="noreferrer">
                View uploaded video
              </a>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
